import React, { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/Header/Header";
import Sidebar from "../components/Sidebar/Sidebar";
import RouteErrorBoundary from "../components/RouteErrorBoundary";
import { Skeleton } from "../ui/States";

const DESKTOP_BREAKPOINT = 1280;
const SIDEBAR_COLLAPSED_KEY = "admin-sidebar-collapsed";

const readCollapsed = () => {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
};

// Skeleton shown inside the content area while a route chunk loads, so the
// shell (sidebar, header) stays put instead of the whole page flashing.
export const PageSkeleton = () => (
  <div className="animate-pulse" aria-hidden="true">
    <Skeleton className="h-6 w-48" />
    <Skeleton className="mt-2 h-3.5 w-80" />
    <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="h-24 rounded-xl border border-slate-200 bg-white" />
      ))}
    </div>
    <div className="mt-6 h-72 rounded-xl border border-slate-200 bg-white" />
  </div>
);

// Persistent application shell. Mounted once by the router; pages render in
// <Outlet/>, so the sidebar/header never remount between internal navigations.
const AdminShell = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= DESKTOP_BREAKPOINT);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const mainRef = useRef(null);
  const openButtonRef = useRef(null);

  useEffect(() => {
    const media = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
    const update = () => {
      setIsDesktop(media.matches);
      if (media.matches) setMobileOpen(false);
    };
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  // Close the mobile drawer on navigation and move focus to the page.
  useEffect(() => {
    setMobileOpen(false);
    if (mainRef.current) {
      mainRef.current.focus({ preventScroll: true });
    }
    window.scrollTo({ top: 0, left: 0 });
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const openButton = openButtonRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKey = (event) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKey);
      openButton?.focus?.();
    };
  }, [mobileOpen]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((previous) => {
      const next = !previous;
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? "1" : "0");
      } catch {}
      return next;
    });
  }, []);

  const sidebarCollapsed = isDesktop && collapsed;

  return (
    <div className="admin-shell min-h-screen bg-surface-muted text-slate-800">
      <a
        href="#admin-main"
        className="sr-only focus:not-sr-only focus:fixed focus:start-3 focus:top-3 focus:z-[100] focus:rounded-lg focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:shadow"
      >
        {t("app.skipToContent")}
      </a>
      <div className="flex min-h-screen">
        <Sidebar
          open={isDesktop || mobileOpen}
          isDesktop={isDesktop}
          collapsed={sidebarCollapsed}
          onToggleCollapsed={toggleCollapsed}
          onClose={() => setMobileOpen(false)}
        />

        {mobileOpen && !isDesktop ? (
          <button
            type="button"
            aria-label={t("header.closeNavigation")}
            className="fixed inset-0 z-40 bg-slate-900/50"
            onClick={() => setMobileOpen(false)}
          />
        ) : null}

        <div className="flex min-h-screen min-w-0 flex-1 flex-col">
          <Header
            onOpenSidenav={() => setMobileOpen(true)}
            openButtonRef={openButtonRef}
            showMenuButton={!isDesktop}
          />
          <main
            id="admin-main"
            ref={mainRef}
            tabIndex={-1}
            className="admin-page relative flex-1 outline-none"
          >
            <div className="mx-auto w-full max-w-[1600px] px-4 py-5 sm:px-6 lg:px-8">
              <RouteErrorBoundary resetKey={location.pathname}>
                <Suspense fallback={<PageSkeleton />}>
                  <Outlet />
                </Suspense>
              </RouteErrorBoundary>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminShell;
